import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { insertGameRoomSchema, type InsertGameRoom } from "@shared/schema";
import { Loader2, GamepadIcon, Users, Eye, User } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useLocation } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";

interface CreateGameDialogProps {
  readonly isOpen: boolean;
  readonly onOpenChange: (open: boolean) => void;
}

export function CreateGameDialog({ isOpen, onOpenChange }: CreateGameDialogProps) {
  const queryClient = useQueryClient();
  const [_, setLocation] = useLocation();
  const { user } = useAuth();
  const { toast } = useToast();

  const { data: friendsList } = useQuery({
    queryKey: ["friends"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/friends");
      if (!res.ok) throw new Error("Failed to fetch friends");
      return res.json();
    },
  });

  const form = useForm<InsertGameRoom>({
    resolver: zodResolver(insertGameRoomSchema),
    mode: "onChange",
    defaultValues: {
      name: "New Game",
      gameType: "public",
      spectatorMode: "private",
      friendId: null
    }
  });

  // Reset form when dialog opens and validate immediately
  useEffect(() => {
    if (isOpen) {
      form.reset({
        name: "New Game",
        gameType: "public",
        spectatorMode: "private",
        friendId: null
      });
      // Trigger validation immediately
      form.trigger();
    }
  }, [isOpen, form]);

  const createGameMutation = useMutation({
    mutationFn: async (data: InsertGameRoom) => {
      console.log('Creating game with data:', data);
      
      try {
        const res = await apiRequest("POST", "/api/game-rooms", data);
        console.log('API response:', res);
        
        if (!res.ok) {
          const error = await res.json();
          console.error('API error:', error);
          throw new Error(error.message ?? "Failed to create game room");
        }
        
        const json = await res.json();
        console.log('Game created successfully:', json);
        return json;
      } catch (err) {
        console.error('Error creating game:', err);
        throw err;
      }
    },
    onError: (error) => {
      console.error('Mutation error:', error);
      toast({
        title: "Error creating game",
        description: error.message || "Failed to create game",
        variant: "destructive",
      });
    },
    onSuccess: async (data) => {
      console.log('Mutation succeeded, game data:', data);
      await queryClient.invalidateQueries({ queryKey: ["game-rooms"] });
      onOpenChange(false);
      setLocation(`/game/${data.id}`);
    },
  });

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="bg-black/95 border border-primary/30 shadow-lg shadow-primary/5">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <GamepadIcon className="h-5 w-5 text-primary" />
            <DialogTitle className="text-xl text-white">Create New Game</DialogTitle>
          </div>
          <DialogDescription className="text-gray-400">
            Configure your game settings below
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              console.log('Form submitted');
              
              if (!user?.id) {
                console.error('No user ID found');
                return;
              }

              const formData = form.getValues();
              const gameData = {
                name: formData.name?.trim() || "",
                gameType: formData.gameType,
                spectatorMode: formData.spectatorMode,
                friendId: formData.gameType === "private" ? formData.friendId : null,
                creator: user.id
              };

              console.log('Submitting game data:', gameData);
              createGameMutation.mutate(gameData);
            }}
            className="space-y-5"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center text-sm text-gray-300">
                    <GamepadIcon className="h-4 w-4 mr-2 text-primary" />
                    Game Name
                  </FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="Enter game name" 
                      {...field}
                      className="bg-zinc-900/90 border-gray-800 text-white" 
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="gameType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center text-sm text-gray-300">
                    <Users className="h-4 w-4 mr-2 text-primary" />
                    Game Type
                  </FormLabel>
                  <Select
                    onValueChange={(value) => {
                      field.onChange(value);
                      if (value !== "private") {
                        form.setValue("friendId", null);
                      }
                    }}
                    defaultValue={field.value}
                  >
                    <SelectTrigger className="bg-zinc-900/90 border-gray-800 text-white">
                      <SelectValue placeholder="Select game type" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-gray-800">
                      <SelectItem value="public">
                        Public Game (Anyone can join)
                      </SelectItem>
                      <SelectItem value="friends">
                        Friends (Any friend can join)
                      </SelectItem>
                      {friendsList?.length > 0 && (
                        <SelectItem value="private">
                          Private (Play with a specific friend)
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />
            {form.watch("gameType") === "private" &&
              friendsList?.length > 0 && (
                <FormField
                  control={form.control}
                  name="friendId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center text-sm text-gray-300">
                        <User className="h-4 w-4 mr-2 text-primary" />
                        Invite Friend
                      </FormLabel>
                      <Select
                        onValueChange={(value) =>
                          field.onChange(Number(value))
                        }
                        defaultValue={undefined}
                      >
                        <SelectTrigger className="bg-zinc-900/90 border-gray-800 text-white">
                          <SelectValue placeholder="Select friend" />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-gray-800">
                          {friendsList
                            ?.filter(
                              (friend: any) => friend.status === "accepted",
                            )
                            .map((friend: any) => (
                              <SelectItem
                                key={friend.id}
                                value={friend.id.toString()}
                              >
                                {friend.username}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />
              )}
            <FormField
              control={form.control}
              name="spectatorMode"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center text-sm text-gray-300">
                    <Eye className="h-4 w-4 mr-2 text-primary" />
                    Spectator Mode
                  </FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <SelectTrigger className="bg-zinc-900/90 border-gray-800 text-white">
                      <SelectValue placeholder="Select spectator mode" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-gray-800">
                      <SelectItem value="private">
                        Private (No Spectators)
                      </SelectItem>
                      <SelectItem value="friends">Friends Only</SelectItem>
                      <SelectItem value="public">
                        Public (Anyone Can Watch)
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />
            <Button 
              type="submit" 
              className="w-full bg-green-600 hover:bg-green-700 text-white font-medium mt-2"
            >
              {createGameMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                "Create Game"
              )}
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}